import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatTimeAgo } from '@tendedloop/shared/i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, QrCode } from 'lucide-react';
import type { AnimalHealthState } from '@tendedloop/shared';
import { HEALTH_FILTERS, HEALTH_COLORS, HEALTH_LABELS } from '@/utils/animalHealth';
import { getTrackIcon } from '@/utils/animalTracks';

interface AnimalDetailModalProps {
  animal: {
    slug: string;
    name: string;
    habitat: string;
    fieldNote: string;
    imagePath: string;
    thumbPath?: string;
    healthState: AnimalHealthState;
    amenityName: string;
    locationName: string | null;
    buildingName: string;
    lastScannedAt: string;
    discoveredAt: string;
    scanCount: number;
    thisMonthScans: number;
    scanDates: string[];
  } | null;
  onClose: () => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function AnimalDetailModal({ animal, onClose }: AnimalDetailModalProps) {
  const { t, i18n } = useTranslation('globe');
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus trap: Tab cycles within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (animal) {
      // Save trigger element for focus restoration
      triggerRef.current = document.activeElement;

      window.gtag?.('event', 'globe_detail_open', {
        animal_slug: animal.slug,
        health_state: animal.healthState,
      });
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Move focus into modal
      requestAnimationFrame(() => {
        const closeBtn = modalRef.current?.querySelector<HTMLElement>('button');
        closeBtn?.focus();
      });

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        // Restore focus to trigger element
        if (triggerRef.current instanceof HTMLElement) {
          triggerRef.current.focus();
        }
      };
    }
  }, [animal, handleKeyDown]);

  const locationParts = animal ? [animal.amenityName, animal.locationName, animal.buildingName].filter(Boolean) : [];

  return (
    <AnimatePresence>
      {animal && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={animal.name}
            className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-background"
            style={{ maxHeight: '92vh' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common:close', { defaultValue: 'Close' })}
              className="absolute top-3 end-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
            >
              <X size={18} />
            </button>

            {/* Scrollable content */}
            <div className="overflow-y-auto">
              {/* Image - progressive: show thumb immediately, upgrade to full-size */}
              <div className="relative w-full bg-stone-100" style={{ aspectRatio: '1/1', maxHeight: '55vh' }}>
                {imgError ? (
                  <div className="flex h-full w-full items-center justify-center bg-muted text-4xl font-bold text-muted-foreground">
                    {animal.name.charAt(0).toUpperCase()}
                  </div>
                ) : (
                  <>
                    {/* Thumb loads instantly as base layer */}
                    {animal.thumbPath && (
                      <img
                        src={animal.thumbPath}
                        alt={animal.name}
                        className={`absolute inset-0 h-full w-full object-cover ${HEALTH_FILTERS[animal.healthState]}`}
                      />
                    )}
                    {/* Full-size overlays once loaded */}
                    <img
                      src={animal.imagePath || animal.thumbPath}
                      alt={animal.name}
                      className={`relative h-full w-full object-cover ${HEALTH_FILTERS[animal.healthState]}`}
                      onError={() => setImgError(true)}
                    />
                  </>
                )}
              </div>

              {/* Details */}
              <div className="flex flex-col gap-4 p-5">
                {/* Name + health badge */}
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-xl font-bold text-foreground">{animal.name}</h2>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium text-white ${HEALTH_COLORS[animal.healthState]}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-white/70" aria-hidden="true" />
                    {t(`detail.status.${animal.healthState}`, {
                      defaultValue: HEALTH_LABELS[animal.healthState],
                    })}
                  </span>
                </div>

                {/* Location */}
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin size={14} className="mt-0.5 shrink-0" />
                  <span>{locationParts.join(' / ')}</span>
                </div>

                {/* Habitat */}
                <span className="inline-flex w-fit items-center rounded-md bg-forest-wash px-2 py-0.5 text-xs font-medium text-forest">
                  {animal.habitat}
                </span>

                {/* Field note */}
                <p className="text-sm italic text-muted-foreground">{animal.fieldNote}</p>

                {/* 30-day trail visualization */}
                {animal.scanDates.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t('hint.trailExplainer', {
                        defaultValue: 'Each dot is one scan day. Denser trails mean more attention.',
                      })}
                    </p>
                    <div
                      className="flex items-center gap-0.5 flex-wrap"
                      role="img"
                      aria-label={`Scanned ${new Set(animal.scanDates.map((d) => new Date(d).toISOString().slice(0, 10))).size} of last 30 days`}
                    >
                      {(() => {
                        const today = new Date();
                        const scanDateSet = new Set(
                          animal.scanDates.map((d) => new Date(d).toISOString().slice(0, 10)),
                        );
                        return Array.from({ length: 30 }, (_, i) => {
                          const day = new Date(today);
                          day.setDate(day.getDate() - (29 - i));
                          const dayStr = day.toISOString().slice(0, 10);
                          const hasData = scanDateSet.has(dayStr);
                          return hasData ? (
                            <img
                              key={dayStr}
                              src={getTrackIcon(animal.slug)}
                              alt=""
                              className="h-3 w-3 opacity-80"
                              title={dayStr}
                            />
                          ) : (
                            <span key={dayStr} className="h-2.5 w-2.5 rounded-full bg-muted" title={dayStr} />
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <StatItem
                    label={t('detail.lastTracked', { defaultValue: 'Last tracked' })}
                    value={formatTimeAgo(animal.lastScannedAt, i18n.language)}
                  />
                  <StatItem
                    label={t('detail.thisMonth', { defaultValue: 'This month', count: animal.thisMonthScans })}
                    value={`${animal.thisMonthScans}`}
                  />
                  <StatItem
                    label={t('detail.totalVisits', { defaultValue: 'Total', count: animal.scanCount })}
                    value={`${animal.scanCount}`}
                  />
                  <StatItem
                    label={t('detail.discovered', { defaultValue: 'Discovered' })}
                    value={formatDate(animal.discoveredAt)}
                  />
                </div>

                {/* Go to scanner */}
                <button
                  type="button"
                  onClick={() => {
                    window.gtag?.('event', 'globe_detail_scan_cta', { animal_slug: animal.slug });
                    onClose();
                    navigate('/scan');
                  }}
                  className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-forest py-3 text-sm font-semibold text-white transition-colors hover:bg-forest/90 active:bg-forest/80"
                >
                  <QrCode size={16} />
                  {t('detail.scanNow', { defaultValue: 'Go to Scanner' })}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/50 p-2.5">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}
